import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { absoluteMediaUrl } from "@/lib/api";

/**
 * Sets document title + meta description + og:title/description/image for a page.
 * `pageKey` maps to translations[lang].seo[pageKey] object with { title, description, ogImage }.
 */
export const SeoHead = ({ pageKey }) => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const seo = t(`seo.${pageKey}`, { returnObjects: true, defaultValue: null });
    if (!seo || typeof seo !== "object") return;

    const setMeta = (selector, attr, value) => {
      if (!value) return;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [k, v] = selector.match(/\[([^=]+)="([^"]+)"\]/).slice(1);
        el.setAttribute(k, v);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    if (seo.title) document.title = seo.title;
    setMeta('meta[name="description"]', "content", seo.description || "");
    setMeta('meta[property="og:title"]', "content", seo.title || "");
    setMeta('meta[property="og:description"]', "content", seo.description || "");
    setMeta('meta[property="og:type"]', "content", "website");
    setMeta('meta[property="og:locale"]', "content", i18n.language);
    setMeta('meta[property="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[property="twitter:title"]', "content", seo.title || "");
    setMeta('meta[property="twitter:description"]', "content", seo.description || "");

    const ogImage = seo.ogImage ? absoluteMediaUrl(seo.ogImage) : "";
    if (ogImage) {
      setMeta('meta[property="og:image"]', "content", ogImage);
      setMeta('meta[property="twitter:image"]', "content", ogImage);
    }
  }, [t, i18n.language, pageKey]);

  return null;
};
